import type {SearchPanelState} from './CrmSearchPanel';

export const CRM_SEARCH_CONTEXT_TTL=5*60*1000;
interface SearchContext {
  principal:string;
  destinationPath?:string;
  sourceKey:string;
  sourcePath:string;
  query:string;
  view:SearchPanelState;
}
const HANDOFF_KEY='crm-search-handoff';
const contexts=new Map<string,{value:SearchContext;expires:number;timer:number}>();
const copy=(value:SearchContext):SearchContext=>({...value,view:{...value.view,
  ...(value.view.otherBranches?{otherBranches:{...value.view.otherBranches}}:{})}});
function remove(token:string){
  const entry=contexts.get(token);if(entry)window.clearTimeout(entry.timer);contexts.delete(token);
  try { const saved=JSON.parse(sessionStorage.getItem(HANDOFF_KEY)??'null');if(saved?.token===token)sessionStorage.removeItem(HANDOFF_KEY); } catch { /* Optional storage may be unavailable. */ }
}
export function persistCrmSearchContext(token:string){
  const entry=contexts.get(token);
  if(!entry || entry.expires<=Date.now())return;
  try {sessionStorage.setItem(HANDOFF_KEY,JSON.stringify({token,value:copy(entry.value),expires:entry.expires}));}catch{/* Optional tab storage; no URL fallback. */}
}

export function clearCrmSearchContexts(){for(const token of contexts.keys())remove(token);try{sessionStorage.removeItem(HANDOFF_KEY);}catch{/* Optional storage. */}}
export function saveCrmSearchContext(value:SearchContext){
  for(const [token,entry] of contexts)if(entry.value.principal!==value.principal || entry.value.sourceKey===value.sourceKey)remove(token);
  while(contexts.size>=8)remove(contexts.keys().next().value!);
  const token=crypto.randomUUID();
  contexts.set(token,{value:copy(value),expires:Date.now()+CRM_SEARCH_CONTEXT_TTL,
    timer:window.setTimeout(()=>remove(token),CRM_SEARCH_CONTEXT_TTL)});
  return token;
}
export function readCrmSearchContext(principal:string,sourceKey:string,sourcePath:string):SearchContext|null{
  for(const [token,entry] of contexts){
    if(entry.expires<=Date.now()){remove(token);continue;}
    if(entry.value.principal===principal && entry.value.sourceKey===sourceKey && entry.value.sourcePath===sourcePath)return copy(entry.value);
  }
  try {
    const saved=JSON.parse(sessionStorage.getItem(HANDOFF_KEY)??'null');
    if(saved && saved.expires>Date.now() && saved.expires<=Date.now()+CRM_SEARCH_CONTEXT_TTL
      && saved.value?.principal===principal && saved.value.sourceKey===sourceKey && saved.value.sourcePath===sourcePath
      && typeof saved.value.query==='string' && saved.value.query.length<=80 && saved.value.view
      && Number.isSafeInteger(saved.value.view.page) && saved.value.view.page>0 && saved.value.view.page<=1000){
      const remaining=saved.expires-Date.now();
      contexts.set(saved.token,{value:copy(saved.value),expires:saved.expires,timer:window.setTimeout(()=>remove(saved.token),remaining)});
      // Retain the same expiring handoff for browser Forward/Back across app reloads.
      return copy(saved.value);
    }
    if(saved && (saved.expires<=Date.now() || saved.value?.principal!==principal))sessionStorage.removeItem(HANDOFF_KEY);
  }catch{/* Malformed/disabled optional storage must never affect navigation. */}
  return null;
}
export function bindCrmSearchContextLifecycle(){
  let expiryTimer:number|undefined;
  try {
    const saved=JSON.parse(sessionStorage.getItem(HANDOFF_KEY)??'null');
    if(saved && typeof saved.token==='string' && saved.expires>Date.now() && saved.expires<=Date.now()+CRM_SEARCH_CONTEXT_TTL){
      expiryTimer=window.setTimeout(()=>remove(saved.token),saved.expires-Date.now());
    }else if(saved)sessionStorage.removeItem(HANDOFF_KEY);
  }catch{/* Optional storage. */}
  const events=['app:logout','aa:navigation-purge'];
  for(const event of events)window.addEventListener(event,clearCrmSearchContexts);
  return ()=>{if(expiryTimer!==undefined)window.clearTimeout(expiryTimer);for(const event of events)window.removeEventListener(event,clearCrmSearchContexts);};
}

/** Navigation only: this never grants record access or supplies record data. */
export function canReturnCrmSearchContext(principal:string,destinationPath:string):boolean {
  for(const entry of contexts.values())if(entry.expires>Date.now() && entry.value.principal===principal && entry.value.destinationPath===destinationPath)return true;
  try {
    const entry=JSON.parse(sessionStorage.getItem(HANDOFF_KEY)??'null');
    return !!entry && entry.expires>Date.now() && entry.expires<=Date.now()+CRM_SEARCH_CONTEXT_TTL && entry.value?.principal===principal && entry.value.destinationPath===destinationPath;
  }catch{return false;}
}
